import Link from 'next/link';
import styled from 'styled-components';
import { mediaScreen } from '../../utils/mediaScreen';

export const PageContainer = styled.div`
  position: relative;
  padding-top: 42px;
  padding-bottom: 10%;

  .desktop-only {
    display: block;
  }

  .mobile-only {
    display: none;
  }

  ${mediaScreen('xs', 'md')} {
    .desktop-only {
      display: none;
    }

    .mobile-only {
      display: block;
    }
  }
`;

export const Hero = styled.div`
  display: flex;
  position: relative;
  padding: 60px 0 70px;
  margin: 0 0 10px;
  color: white;
  text-align: center;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: radial-gradient(
      circle,
      rgba(255, 255, 255, 0.1) 5%,
      transparent 5%
    );
    background-size: 50px 50px;
    pointer-events: auto;
  }

  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 50%;
    background: linear-gradient(
      to bottom,
      rgba(24, 30, 43, 0),
      rgba(24, 30, 43, 1)
    );

    pointer-events: auto;
  }

  > * {
    z-index: 1;
  }
`;

export const HeroTitle = styled.h1`
  font-size: 67px;
  margin: 0;

  strong {
    color: white;
  }

  ${mediaScreen('xs', 'md')} {
    font-size: 40px;
    margin-bottom: 15px;
  }
`;

export const HeroText = styled.p`
  font-size: 22px;
  line-height: 1.5;
  margin: 0 0 20px;
  color: rgba(255, 255, 255, 0.8);

  ${mediaScreen('xs', 'md')} {
    font-size: 16px;
    margin-bottom: -20px;
  }
`;

export const CheckboxList = styled.ul`
  margin: 0;
  padding: 0;
`;

export const CheckboxListItem = styled.li`
  font-size: 16px;
  line-height: 1;
  color: white;
  list-style-type: none;

  &:not(:last-child) {
    margin-bottom: 25px;
  }

  label {
    cursor: pointer;
    display: flex;
    align-items: center;
  }

  input {
    display: none;
  }
`;

export const CheckboxListInput = styled.span<{ isChecked: boolean }>`
  position: relative;
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 1px solid;
  border-color: ${(props) =>
    props.isChecked ? props.theme.colors.teal : '#b4b4b3'};
  background-color: ${(props) =>
    props.isChecked ? props.theme.colors.teal : 'transparent'};
  margin-right: 10px;
  border-radius: 3px;

  ::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 8px;
    height: 8px;
    background-color: #fff;
    border-radius: 50%;
    display: ${(props) => (props.isChecked ? 'block' : 'none')};
  }
`;

export const ConnectorCardTitle = styled.h2`
  font-size: 18px;
  font-weight: 400;
  margin: 0;
  margin-top: -4px;
  margin-bottom: 5px;
  display: flex;
  justify-content: space-between;
`;

export const ConnectorCardSubTitle = styled.h4`
  font-size: 16px;
  font-weight: 400;
  margin: 0;
  color: #fff;
  margin-top: 20px;
`;

export const ConnectorCardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding-left: 5px;

  ul {
    margin-bottom: 0;
  }
`;

export const ConnectorCardDescription = styled.div<{ lineClamp?: number }>`
  color: #b4b4b3;
  margin-bottom: 6px;

  img,
  ul,
  li {
    display: none;
  }

  * {
    margin: 0 !important;
    padding: 0 !important;
    font-size: 15px !important;
  }

  > * {
    overflow: ${(props) => (props.lineClamp ? 'hidden' : 'visible')};
    display: ${(props) => (props.lineClamp ? '-webkit-box' : 'block')};
    -webkit-line-clamp: ${(props) => props.lineClamp || 'unset'};
    -webkit-box-orient: vertical;
  }
`;

export const ConnectorCardTag = styled.div`
  font-size: 11px;
  color: ${(props) => props.theme.colors.teal};
  padding: 4px 8px;
  border: 1px solid currentColor;
  border-radius: 100px;
  flex-shrink: 0;
  cursor: pointer;
  margin-top: 10px;
`;

export const ConnectorCardTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: auto;

  ${ConnectorCardTag}:not(:last-child) {
    margin-right: 10px;
  }
`;

export const ConnectorCardLogo = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 5px;
  overflow: hidden;
  background-color: white;
  flex-shrink: 0;

  div,
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  & + ${ConnectorCardContent} {
    margin-left: 12px;
  }
`;

export const ConnectorCard = styled.div`
  display: flex;
  border-radius: 5px;
  padding: 16px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  transition: border 0.3s ease-in-out;
  height: 100%;
  background: ${(props) => props.theme.colors.slate2};
  border: 2px solid transparent;

  a > & {
    &:hover {
      border: 2px solid ${(props) => props.theme.colors.teal};
    }

    ${ConnectorCardTitle} {
      color: white;
    }
  }
`;

export const ProductSection = styled.div`
  ${mediaScreen('xs', 'md')} {
    padding: 66px 0 0;
  }

  ${ConnectorCard} {
    padding: 40px;

    ${mediaScreen('xs', 'md')} {
      flex-direction: column;
      padding: 16px;
    }
  }

  ${ConnectorCardLogo} {
    width: 150px;
    height: 150px;

    ${mediaScreen('xs', 'md')} {
      width: 100px;
      height: 100px;
      margin: -66px auto 12px;
    }

    & + ${ConnectorCardContent} {
      margin-left: 24px;

      ${mediaScreen('xs', 'md')} {
        margin: 0;
      }
    }
  }

  ${ConnectorCardTitle} {
    margin-bottom: 8px;

    ${mediaScreen('xs', 'md')} {
      text-align: center;
    }
  }

  ${ConnectorCardSubTitle} {
    font-size: 24px;
  }

  ${ConnectorCardTags} {
    ${mediaScreen('xs', 'md')} {
      margin: 0 auto;
    }
  }
`;

export const SideNav = styled.nav``;

export const SideNavTitle = styled.h4`
  font-size: 24px;
  color: white;
  margin: 0 0 16px;
`;

export const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  color: ${(props) => props.theme.colors.teal};

  :hover {
    text-decoration: underline;
  }

  svg {
    color: ${(props) => props.theme.colors.teal};
  }
`;

export const OtherConnectors = styled.div``;

export const OtherConnectorsTitle = styled.h2`
  font-size: 32px;
  color: #fff;
  text-align: center;
  margin: 0 0 40px;

  ${mediaScreen('xs', 'md')} {
    font-size: 24px;
  }
`;
